import noop from 'lodash/noop';
import { Link } from 'react-router-dom';
import React, { FC } from 'react';
import styled from 'styled-components';

import { ActionType, SetPriorityAction } from 'src/actions/rules/actions';
import { Button } from 'src/presentation/components/agent_desktop_components';
import Footer from 'src/presentation/components/forms/Footer';
import { getLabelForPriority } from './actions/SetPriorityAction';
import { H1, H4 } from '@ad-scripts/presentation/components/common/headers';
import { Rule } from 'src/actions/rules/rulesResource';
import ruleRevision from 'src/actions/rules/ruleRevision';
import { StyledBody } from './shared/StyledComponents';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import RuleConditions from './RuleConditions';

type SmartMatchRuleRevisionProps = {
  revisionId: string;
};

const dataAidPrefix = 'admin-smartMatch-ruleRevision';

const SmartMatchRuleRevision: FC<SmartMatchRuleRevisionProps> = ({ revisionId }) => {
  const { loadingState, value } = useDocumentResource(ruleRevision, {
    fetch: true,
    params: { revisionId },
  });

  if (loadingState !== 'succeeded') {
    return null;
  }

  const rule = new Rule(value);
  const setPriorityAction = rule.actions[ActionType.SetPriority];

  return (
    <>
      <H1 data-aid={`${dataAidPrefix}-header-title`}>View Priority Boost</H1>
      <StyledBody>
        <StyledSection>
          <StyledRuleRevisionRowLabel data-aid={`${dataAidPrefix}-name-section-header`}>
            Name
          </StyledRuleRevisionRowLabel>
          <StyledRuleRevisionRowValue data-aid={`${dataAidPrefix}-name-section-value`}>
            {rule.name}
          </StyledRuleRevisionRowValue>
        </StyledSection>
        <RuleConditions
          data-aid={`${dataAidPrefix}-ruleConditions`}
          conditions={rule.conditions}
          title="Criteria"
          errors={[]}
          isReadOnly
          triggerType={rule.trigger.type}
          onAddCondition={noop}
          onDeleteCondition={noop}
          onUpdateCondition={noop}
        />
        <StyledSection>
          <StyledRuleRevisionRowLabel data-aid={`${dataAidPrefix}-priority-section-header`}>
            Boost size
          </StyledRuleRevisionRowLabel>
          <StyledRuleRevisionRowValue data-aid={`${dataAidPrefix}-priority-section-value`}>
            {getLabelForPriority((setPriorityAction as SetPriorityAction).priority)}
          </StyledRuleRevisionRowValue>
        </StyledSection>
      </StyledBody>
      <Footer>
        <StyledEditRuleLink data-aid={`${dataAidPrefix}-editLink`} to={`/people-match/${rule.id}`}>
          Edit
        </StyledEditRuleLink>
        <Button data-aid={`${dataAidPrefix}-backButton`} onClick={() => window.history.back()}>
          Back
        </Button>
      </Footer>
    </>
  );
};

const StyledEditRuleLink = styled(Link)`
  border: none;
  background: transparent;
  color: ${p => p.theme.colors.gray600};
  cursor: pointer;
  display: inline-block;
  line-height: ${p => p.theme.lineHeight.controls};
  padding: ${p => p.theme.spacing.medium} ${p => p.theme.spacing.large};
  text-align: center;
  white-space: nowrap;
  &:hover {
    color: ${p => p.theme.colors.green300};
  }
`;

const StyledRuleRevisionRowLabel = styled(H4)`
  font-weight: bold;
  margin-bottom: ${p => p.theme.spacing.large};
`;

const StyledRuleRevisionRowValue = styled.div`
  font-size: 14px;
  margin-top: 1px;
`;

const StyledSection = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: ${p => p.theme.borderRadius.large};
  margin-bottom: ${p => p.theme.spacing.insetSmall};
  padding: ${p => p.theme.spacing.stackMedium} ${p => p.theme.spacing.insetLarge};
`;

export default SmartMatchRuleRevision;
